import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { CalendarDays, MapPin, Users, DollarSign, Trash2, Heart, ArrowLeft, Eye, ChevronDown, ChevronUp, ExternalLink } from "lucide-react";
import { formatDate } from "@/modules/calculator";
import type { ArtistWithShows, ShowWithDetails } from "@shared/schema";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { FaSpotify, FaInstagram, FaTiktok, FaFacebook, FaYoutube, FaTwitter } from "react-icons/fa";
import ShowDetail from "@/components/show-detail";
import { ShowListItem } from "@/components/show-list-item";
import { ShowListHeader } from "@/components/show-list-header";
import { SocialMediaWidget } from "@/components/social-media-widget";
import { PlanStorage } from "@/lib/plan-storage";
import { usePlanStore } from "@/stores/plan-store";
import { useAuth } from "@/lib/auth";
import { usePersona } from "@/contexts/persona-context";
import { trimShowsByFilters, filterShowsByDma, type ShowFilterConfig } from "@shared/filter-engine";

interface ArtistModalProps {
  artist: ArtistWithShows | null;
  isOpen: boolean;
  onClose: () => void;
  onAddToProposal?: (show: ShowWithDetails) => void;
  onBulkAddToProposal?: (shows: ShowWithDetails[]) => void;
  onRemoveShow?: (showId: number) => void;
  onSaveShow?: (show: ShowWithDetails) => void;
  isAuthenticated?: boolean;
  showRemoveButtons?: boolean;
  savedShows?: ShowWithDetails[];
  currentFilters?: {
    dateFrom?: string;
    dateTo?: string;
    genre?: string;
    location?: string;
    artistName?: string;
    venueName?: string;
  };
}

export default function ArtistModal({ artist, isOpen, onClose, onAddToProposal, onBulkAddToProposal, onRemoveShow, onSaveShow, isAuthenticated = false, showRemoveButtons = false, savedShows = [], currentFilters }: ArtistModalProps) {
  const [selectedShows, setSelectedShows] = useState<Set<number>>(new Set());
  const [viewingShow, setViewingShow] = useState<ShowWithDetails | null>(null);
  const [isBioExpanded, setIsBioExpanded] = useState(false);

  const { toast } = useToast();
  const { user } = useAuth();
  const { activePersona } = usePersona();

  // Subscribe to plan store reactively for package info
  const planPackages = usePlanStore(state => state.getPackages(user?.id));

  // Reset viewingShow state when artist changes or modal opens
  useEffect(() => {
    if (isOpen && artist) {
      setViewingShow(null);
      setSelectedShows(new Set());
      setIsBioExpanded(false);
    }
  }, [isOpen, artist?.id]);
  const queryClient = useQueryClient();

  if (!artist) return null;

  const formatFollowerCount = (count: number) => {
    if (count >= 1000000) {
      return `${(count / 1000000).toFixed(1)}M`;
    } else if (count >= 1000) {
      return `${(count / 1000).toFixed(1)}K`;
    }
    return count.toLocaleString();
  };

  const extractSpotifyId = (url: string) => {
    const match = url.match(/artist\/([a-zA-Z0-9]+)/);
    return match ? match[1] : url.split('/').pop();
  };

  // Filter shows based on current filters using shared functions
  const sortedUpcomingShows = (() => {
    const showConfig: ShowFilterConfig = currentFilters ? {
      dateFrom: currentFilters.dateFrom,
      dateTo: currentFilters.dateTo,
      genre: currentFilters.genre,
      location: currentFilters.location,
      artistName: currentFilters.artistName,
      venueName: currentFilters.venueName,
    } : {};
    let filtered = trimShowsByFilters(artist.upcomingShows || [], showConfig, { excludePastShows: false });
    filtered = filterShowsByDma(filtered, activePersona?.targetDmaIds);
    return filtered;
  })()
    .sort((a, b) => {
      const dateCompare = ((a.date as any as string).slice(0,10)).localeCompare((b.date as any as string).slice(0,10));
      if (dateCompare !== 0) return dateCompare;
      const nameA = (a.artist?.name || '').toLowerCase();
      const nameB = (b.artist?.name || '').toLowerCase();
      return nameA.localeCompare(nameB);
    });

  const sponsorableShows = sortedUpcomingShows.filter(show => show.sponsorable);
  
  const handleShowSelection = (showId: number, checked: boolean) => {
    const newSelection = new Set(selectedShows);
    if (checked) {
      newSelection.add(showId);
    } else {
      newSelection.delete(showId);
    }
    setSelectedShows(newSelection);
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const sponsorableIds = new Set(sponsorableShows.map(show => show.id));
      setSelectedShows(sponsorableIds);
    } else {
      setSelectedShows(new Set());
    }
  };

  const selectedCount = selectedShows.size;
  const allSponsorableSelected = sponsorableShows.length > 0 && sponsorableShows.every(show => selectedShows.has(show.id));

  const handleBulkAdd = () => {
    if (onBulkAddToProposal && selectedCount > 0) {
      const selectedShowsArray = sortedUpcomingShows.filter(show => selectedShows.has(show.id));
      onBulkAddToProposal(selectedShowsArray);
      setSelectedShows(new Set()); // Clear selection after adding
    }
  };

  const handleViewShow = (show: ShowWithDetails) => {
    setViewingShow(show);
  };

  const handleBackToArtist = () => {
    setViewingShow(null);
  };

  // Extract unique genres - genres is an array from the database
  const genreArray = (artist.genres && Array.isArray(artist.genres)) 
    ? artist.genres.slice(0, 2) 
    : [];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[95vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="sr-only">Artist Details</DialogTitle>
          <DialogDescription className="sr-only">
            View artist information, social media, and upcoming shows
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Show Detail View */}
          {viewingShow ? (
            <ShowDetail
              show={viewingShow}
              onBack={handleBackToArtist}
              showBackButton={true}
              backToText={`Back to ${artist.name}`}
              isAuthenticated={isAuthenticated}
            />
          ) : (
            <>
              {/* Artist Header */}
              <div className="flex flex-col sm:flex-row gap-6">
                <div className="flex-shrink-0">
                  <img
                    src={artist.imageUrl || "https://max.live/hubfs/2Crop-Cell-phone-concert-copy.jpg"}
                    alt={artist.name}
                    className="w-32 h-32 sm:w-40 sm:h-40 object-cover rounded-lg"
                    onError={(e) => {
                      const img = e.target as HTMLImageElement;
                      if (img.src !== "https://max.live/hubfs/2Crop-Cell-phone-concert-copy.jpg") {
                        img.src = "https://max.live/hubfs/2Crop-Cell-phone-concert-copy.jpg";
                      }
                    }}
                  />
                </div>
                
                <div className="flex-1 space-y-4">
                  {/* Artist Info Section */}
                  <div>
                    <h2 className="text-3xl font-bold mb-4 text-foreground">{artist.name}</h2>
                    
                    <div className="space-y-2 text-sm text-muted-foreground">
                      {artist.careerStage && (
                        <div className="flex items-center gap-1.5">
                          <Users className="h-4 w-4" />
                          <span>{artist.careerStage}</span>
                        </div>
                      )}
                      {artist.city && artist.state && (
                        <div className="flex items-center gap-1.5">
                          <MapPin className="h-4 w-4" />
                          <span>{artist.city}, {artist.state}</span>
                        </div>
                      )}
                      {genreArray.length > 0 && (
                        <div className="flex items-center gap-1.5">
                          <span className="text-xs">🎵</span>
                          <span>{genreArray.join(', ')}</span>
                        </div>
                      )}
                      <div className="flex items-center gap-1.5">
                        <CalendarDays className="h-4 w-4" />
                        <span>{(artist.upcomingShows || []).length} upcoming shows</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Artist Bio Section - Full Width */}
              <div className="border-t border-gray-100 pt-6">
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-foreground tracking-tight">About</h3>
                  {(artist.bio && typeof artist.bio === 'string') ? (
                    <div className="space-y-3">
                      <div className="prose prose-sm max-w-none">
                        <p className="text-foreground/80 leading-relaxed text-base font-normal">
                          {isBioExpanded 
                            ? artist.bio 
                            : artist.bio.split(' ').slice(0, 40).join(' ')
                          }
                          {!isBioExpanded && artist.bio.split(' ').length > 40 && '...'}
                        </p>
                      </div>
                      {artist.bio.split(' ').length > 40 && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setIsBioExpanded(!isBioExpanded)}
                          className="h-auto p-0 text-primary hover:text-primary/80 hover:bg-transparent transition-colors btn-text-inline focus:outline-1 focus:outline-offset-1"
                        >
                          <div className="flex items-center gap-1 text-sm font-medium">
                            {isBioExpanded ? (
                              <>
                                <ChevronUp className="h-3.5 w-3.5" />
                                Show Less
                              </>
                            ) : (
                              <>
                                <ChevronDown className="h-3.5 w-3.5" />
                                Read More
                              </>
                            )}
                          </div>
                        </Button>
                      )}
                    </div>
                  ) : (
                    <div className="py-3">
                      <p className="text-muted-foreground text-sm italic">No biography available for this artist</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Minimalist Artist Stats Bar */}
              <div className="bg-gradient-to-br from-slate-50 to-white rounded-xl p-4 border border-slate-200">
                <div className="flex items-center justify-between gap-4 flex-wrap">
                  {/* Social Stats - Left Side */}
                  <div className="flex items-center gap-6 flex-wrap">
                    {artist.spotifyFollowers && (
                      <div className="flex items-center gap-2">
                        <FaSpotify className="w-5 h-5 text-green-500" />
                        <span className="text-sm font-medium text-slate-700">
                          {formatFollowerCount(artist.spotifyFollowers)} followers
                        </span>
                      </div>
                    )}
                    {artist.spotifyMonthlyListeners && (
                      <div className="flex items-center gap-2">
                        <div className="w-5 h-5 bg-green-500 rounded-full flex items-center justify-center">
                          <span className="text-white text-xs">▶</span>
                        </div>
                        <span className="text-sm font-medium text-slate-700">
                          {formatFollowerCount(artist.spotifyMonthlyListeners)} monthly listeners
                        </span>
                      </div>
                    )}
                    {artist.instagramFollowers && (
                      <div className="flex items-center gap-2">
                        <FaInstagram className="w-5 h-5 text-pink-500" />
                        <span className="text-sm font-medium text-slate-700">
                          {formatFollowerCount(artist.instagramFollowers)}
                        </span>
                      </div>
                    )}
                    {artist.tiktokFollowers && (
                      <div className="flex items-center gap-2">
                        <FaTiktok className="w-5 h-5 text-black" />
                        <span className="text-sm font-medium text-slate-700">
                          {formatFollowerCount(artist.tiktokFollowers)}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Spotify Link - Right Side */}
                  {artist.spotifyUrl && (
                    <a
                      href={artist.spotifyUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 bg-green-500 text-white px-4 py-2 rounded-full text-sm font-medium hover:bg-green-600 transition-colors whitespace-nowrap"
                    >
                      <FaSpotify className="w-4 h-4" />
                      Listen on Spotify
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </div>
              </div>

              {/* Upcoming Shows */}
              <div>
                <ShowListHeader
                  title="Upcoming Shows"
                  totalShows={sortedUpcomingShows.length}
                  sponsorableShows={sponsorableShows.length}
                  selectedCount={selectedCount}
                  allSponsorableSelected={allSponsorableSelected}
                  onSelectAll={handleSelectAll}
                  onBulkSaveShows={handleBulkAdd}
                  isAuthenticated={isAuthenticated}
                  showBulkActions={true}
                />
                
                <div className="grid gap-3">
                  {sortedUpcomingShows.length === 0 ? (
                    <p className="text-muted-foreground">No upcoming shows</p>
                  ) : (
                    sortedUpcomingShows.map((show) => {
                      // Check if show is in plan - read from reactive store
                      const packageInfo = planPackages[show.id];
                      const isInPlan = !!packageInfo;

                      return (
                        <ShowListItem
                          key={`show-${show.id}`}
                          show={show}
                          isSelectable={true}
                          isSelected={selectedShows.has(show.id)}
                          onSelectionChange={handleShowSelection}
                          onViewShow={handleViewShow}
                          onRemoveShow={onRemoveShow}
                          isAuthenticated={isAuthenticated}
                          showArtistInfo={false}
                          showVenueInfo={true}
                          showRemoveButton={showRemoveButtons}
                          // Plan status props
                          isInPlan={isInPlan}
                          planPackageTier={packageInfo?.tier}
                          planPackagePrice={packageInfo?.price}
                        />
                      );
                    })
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
